import { logger } from "@/src/lib/logging/logger";

export interface RetryStrategy {
  shouldRetry: (error: any) => boolean;
  maxRetries: number;
}

export interface RetryDeadline {
  expiresAtMs: number;
  createError: () => Error;
}

const GENERIC_ERROR_CODES = [
  "40001", // serialization_failure
  "40P01", // deadlock_detected
  "53300", // too_many_connections
  "57P01", // admin_shutdown -- connection killed by a pooler restart or failover
  "57P03", // cannot_connect_now -- server still starting up
  "08006", // connection_failure
  "08003", // connection_does_not_exist
  "-1", // network error
  "521", // Cloudflare origin down / Supabase transient outage
  "503", // service unavailable
  "PGRST003", // PostgREST connection pool timeout
  "ECONNRESET", // connection reset
  "ENOTFOUND", // DNS resolution failed
  "ETIMEDOUT", // connection timeout
  "ECONNREFUSED", // connection refused
  "OPTIMISTIC_LOCK_CONFLICT", // optimistic lock conflict
];

const GENERIC_ERROR_MESSAGES = [
  "connection timeout",
  "connection terminated unexpectedly",
  "timeout exceeded when trying to connect", // pg-pool, when no connection frees up in time
  "too many clients", // Postgres, when the connection limit is reached
  "fetch failed",
  "network",
  "econnreset",
  "enotfound",
  "etimedout",
  "network connection lost",
  "web server is down",
  "error code 521",
  "gateway error",
];

function isRetryableGenericError(error: unknown): boolean {
  const seen = new Set<unknown>();
  let candidate: unknown = error;

  while (typeof candidate === "object" && candidate !== null && !seen.has(candidate)) {
    seen.add(candidate);

    const { cause, code, message } = candidate as { cause?: unknown; code?: unknown; message?: unknown };
    const normalizedMessage = typeof message === "string" ? message.toLowerCase() : "";

    if (
      (typeof code === "string" && GENERIC_ERROR_CODES.includes(code)) ||
      GENERIC_ERROR_MESSAGES.some((retryableMessage) => normalizedMessage.includes(retryableMessage))
    ) {
      return true;
    }

    candidate = cause;
  }

  return false;
}

export const GENERIC_RETRY_STRATEGY: RetryStrategy = {
  shouldRetry: isRetryableGenericError,
  maxRetries: 3,
};

export const ECW_FHIR_RETRY_STRATEGY: RetryStrategy = {
  shouldRetry: (error) =>
    GENERIC_RETRY_STRATEGY.shouldRetry(error) || [400, 408, 429, 500, 502, 503, 504].includes(error?.response?.status),
  maxRetries: 3,
};

const GEMINI_TRANSIENT_STATUSES = [429, 500, 502, 503, 504];

export const GEMINI_RETRY_STRATEGY: RetryStrategy = {
  shouldRetry: (error) =>
    GENERIC_RETRY_STRATEGY.shouldRetry(error) ||
    [error?.status, error?.statusCode, error?.response?.status].some((status) =>
      GEMINI_TRANSIENT_STATUSES.includes(status)
    ) ||
    error?.message?.includes("UNAVAILABLE"),
  maxRetries: 4,
};

export const ATHENA_RETRY_STRATEGY: RetryStrategy = {
  shouldRetry: (error) => error?.response?.status === 404 || error?.status === 404,
  maxRetries: 7,
};

export async function withRetry<T>(
  name: string,
  operation: () => Promise<{ data: T | null; error: any | null }>,
  { strategy = GENERIC_RETRY_STRATEGY, deadline }: { strategy?: RetryStrategy; deadline?: RetryDeadline } = {}
): Promise<{ data: T | null; error: any | null }> {
  const remainingMs = (): number | undefined =>
    deadline === undefined ? undefined : Math.max(0, deadline.expiresAtMs - Date.now());
  const assertWithinDeadline = (): number | undefined => {
    const remaining = remainingMs();

    if (remaining !== undefined && remaining <= 0) throw deadline!.createError();

    return remaining;
  };

  const handleBackoff = async (attempt: number, error: any) => {
    const backoffMs = exponentialBackoffSeconds(attempt, { baseSeconds: 2 }) * 1000;
    const remaining = assertWithinDeadline();

    if (remaining !== undefined && backoffMs >= remaining) throw deadline!.createError();

    logger.info(`Retrying ${name} in ${backoffMs}ms after error:`, { error });

    await new Promise((resolve) => setTimeout(resolve, backoffMs));
  };

  for (let attempt = 0; attempt < strategy.maxRetries; attempt++) {
    try {
      assertWithinDeadline();
      const result = await operation();

      if (result.error) {
        logger.warn(`Caught error while retrying operation ${name} in withRetry`, {
          error: result.error,
        });

        if (deadline && remainingMs() === 0) throw deadline.createError();

        if (!strategy.shouldRetry(result.error) || attempt === strategy.maxRetries - 1) {
          return result;
        }

        await handleBackoff(attempt, result.error);
        continue;
      }

      return result;
    } catch (error: any) {
      logger.warn(`Uncaught error while retrying operation ${name} in withRetry`, {
        error,
      });

      if (deadline && remainingMs() === 0) throw deadline.createError();

      if (!strategy.shouldRetry(error) || attempt === strategy.maxRetries - 1) {
        throw error;
      }

      await handleBackoff(attempt, error);
    }
  }

  return { data: null, error: new Error(`${name} failed after ${strategy.maxRetries} attempts`) };
}

/**
 * `withRetry` for operations that report failure by throwing rather than returning
 * `{ data, error }`, such as Drizzle queries.
 */
export async function withRetryThrowing<T>(
  name: string,
  operation: () => Promise<T>,
  options: { strategy?: RetryStrategy; deadline?: RetryDeadline } = {}
): Promise<T> {
  const { data, error } = await withRetry<T>(name, async () => ({ data: await operation(), error: null }), options);

  if (error) {
    throw error;
  }

  // Every failing path in withRetry throws, so reaching here means the operation resolved.
  return data as T;
}

export function exponentialBackoffSeconds(
  attempt: number,
  {
    baseSeconds = 10,
    capSeconds = 300,
    growthFactor = 2,
  }: { baseSeconds?: number; capSeconds?: number; growthFactor?: number } = {}
): number {
  const nominal = Math.min(capSeconds, baseSeconds * Math.pow(growthFactor, attempt));

  return Math.round(nominal * (0.5 + Math.random() * 0.5));
}
